// External Intake & Collection v1.0 PR 2 — create/edit page for a
// CollectionTemplate. Renders the kind-specific configuration (Form
// builder / Upload instructions / E-Sign template ref), the common
// fields (name, description, document tag, signature path), and the
// two prompts panel (only after the template has been saved).

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save } from "lucide-react";
import { Button, Card, CardContent, Input, Textarea } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/components/ui/Toast";
import {
  SPECIFIC_COLLECTION_AUDIENCES,
  collectionAudienceLabel,
  collectionPromptsApi,
  collectionTemplatesApi,
  normalizeAudiences,
  type CollectionItemKind,
  type CollectionTemplateDto,
  type CreateCollectionTemplatePayload,
  type FormAttachmentDescriptor,
  type MaterialAudience,
  type SignaturePath,
  type UpdateCollectionTemplatePayload,
} from "@/api/collectionTemplates";
import { documentTagsApi } from "@/api/reportsV32";
import {
  parseFormSchema,
  type FormSchema,
} from "@/lib/formSchema";
import { CollectionFormBuilder } from "@/components/Collection/CollectionFormBuilder";
import { CollectionPromptEditor } from "@/components/Collection/CollectionPromptEditor";
import { SpecialFormEditor } from "@/components/Collection/SpecialFormEditor";
import { ApprovalChainEditor } from "@/components/Collection/ApprovalChain/ApprovalChainEditor";
import {
  chainSignature,
  validateChain,
} from "@/components/Collection/ApprovalChain/approvalChainRules";
import type { ApprovalChainDefinition } from "@/api/approvalChains";
import { useOptionalAuth } from "@/hooks/useAuth";
import { TemplateFormAttachmentCard } from "./TemplateFormAttachmentCard";

interface EducationContentState {
  blobPath: string;
  contentType: string;
  sizeBytes: number;
  originalFileName: string;
}

interface FormState {
  name: string;
  description: string;
  kind: CollectionItemKind;
  // Collection Templates — Audience Multiselect. The checked SPECIFIC audiences
  // (the "All" master state is derived: allChecked ⟺ all seven are present).
  audiences: MaterialAudience[];
  documentTagCode: string;
  instructions: string;
  schema: FormSchema;
  eSignProvider: string;
  eSignTemplateRef: string;
  signerRoleName: string;
  requiresSignature: boolean;
  signaturePath: SignaturePath | "";
  educationContent: EducationContentState | null;
  educationVideoUrl: string;
  educationMinDurationSeconds: string;
  educationIncludeQuestions: boolean;
  feedbackEnabled: boolean;
  assessmentEnabled: boolean;
  isSpecialForm: boolean;
  bodyTemplateMarkdown: string;
  /** Whether the letter body was actually edited this session. The update
   *  payload includes bodyTemplateMarkdown ONLY when true — supplying the
   *  key is an explicit (admin-gated) body edit server-side; omitting it
   *  carries the current body forward. */
  bodyDirty: boolean;
  /** Approval chains (D3) — the edited chain (null = none) and the
   *  borrower-facing flag. The payload carries either ONLY when it differs
   *  from the saved version (chainChanges): supplying them is an admin-gated
   *  edit server-side and 403s non-holders of ManageApprovalChains. */
  approvalChain: ApprovalChainDefinition | null;
  isBorrowerFacing: boolean;
  /** Upload templates (U6a) — a document uploaded in this session, attached
   *  (or replacing the saved one) on save. */
  formAttachmentUpload: FormAttachmentDescriptor | null;
  /** The saved version's document was removed in this session. */
  formAttachmentRemoved: boolean;
  /** The download button's label ("" = "Download form"). */
  formAttachmentButtonLabel: string;
}

const EMPTY_SCHEMA: FormSchema = { fields: [], version: "v1" };

const DEFAULT_FORM: FormState = {
  name: "",
  description: "",
  kind: "Upload",
  // New templates open with an empty selection; at least one audience is
  // required before save (spec §3).
  audiences: [],
  documentTagCode: "",
  instructions: "",
  schema: EMPTY_SCHEMA,
  eSignProvider: "DocuSign",
  eSignTemplateRef: "",
  signerRoleName: "",
  requiresSignature: false,
  signaturePath: "",
  educationContent: null,
  educationVideoUrl: "",
  educationMinDurationSeconds: "",
  educationIncludeQuestions: false,
  // UAT #19 — default on for new templates; the create payload flips
  // these off for ESign (spec §8 decision #5) once kind is selected.
  feedbackEnabled: true,
  assessmentEnabled: true,
  isSpecialForm: false,
  bodyTemplateMarkdown: "",
  bodyDirty: false,
  approvalChain: null,
  isBorrowerFacing: false,
  formAttachmentUpload: null,
  formAttachmentRemoved: false,
  formAttachmentButtonLabel: "",
};

/** Only a Special form (generated letter) or an E-sign template may carry an
 *  approval chain (server: CollectionTemplateService.ValidateApprovalChain). */
function chainEligible(state: Pick<FormState, "kind" | "isSpecialForm">): boolean {
  return (state.kind === "Form" && state.isSpecialForm) || state.kind === "ESign";
}

/** The approval-chain members the save may carry — ONLY what the user changed
 *  against `saved` (null for a new template). Unchanged → nothing, so a
 *  non-admin save never trips the ManageApprovalChains gate. */
function chainChanges(
  state: FormState,
  saved: CollectionTemplateDto | null,
): {
  approvalChain?: ApprovalChainDefinition;
  clearApprovalChain?: boolean;
  isBorrowerFacing?: boolean;
} {
  const out: {
    approvalChain?: ApprovalChainDefinition;
    clearApprovalChain?: boolean;
    isBorrowerFacing?: boolean;
  } = {};
  const savedChain = saved?.approvalChain ?? null;
  if (chainSignature(state.approvalChain) !== chainSignature(savedChain)) {
    if (state.approvalChain) out.approvalChain = state.approvalChain;
    else if (savedChain) out.clearApprovalChain = true;
  }
  if (state.isBorrowerFacing !== (saved?.isBorrowerFacing ?? false)) {
    out.isBorrowerFacing = state.isBorrowerFacing;
  }
  return out;
}

function fromDto(t: CollectionTemplateDto): FormState {
  const educationContent: EducationContentState | null =
    t.educationContentBlobPath && t.educationContentType
      ? {
          blobPath: t.educationContentBlobPath,
          contentType: t.educationContentType,
          sizeBytes: t.educationContentSizeBytes ?? 0,
          originalFileName: t.educationContentOriginalFileName ?? "",
        }
      : null;
  return {
    name: t.name,
    description: t.description ?? "",
    kind: t.kind,
    // A stored ["All"] shows every specific box (and the All master) ticked;
    // otherwise just the specifics named in the set.
    audiences: t.audiences.includes("All")
      ? [...SPECIFIC_COLLECTION_AUDIENCES]
      : SPECIFIC_COLLECTION_AUDIENCES.filter((a) => t.audiences.includes(a)),
    documentTagCode: t.documentTagCode ?? "",
    instructions: t.instructions ?? "",
    schema: parseFormSchema(t.formSchemaJson) ?? EMPTY_SCHEMA,
    eSignProvider: t.eSignProvider ?? "DocuSign",
    eSignTemplateRef: t.eSignTemplateRef ?? "",
    signerRoleName: t.signerRoleName ?? "",
    requiresSignature: t.requiresSignature,
    signaturePath: t.signaturePath ?? "",
    educationContent,
    educationVideoUrl: t.educationVideoUrl ?? "",
    educationMinDurationSeconds:
      t.educationMinimumDurationSeconds == null
        ? ""
        : String(t.educationMinimumDurationSeconds),
    educationIncludeQuestions:
      t.kind === "Education" && (parseFormSchema(t.formSchemaJson)?.fields.length ?? 0) > 0,
    feedbackEnabled: t.feedbackEnabled,
    assessmentEnabled: t.assessmentEnabled,
    isSpecialForm: t.isSpecialForm,
    bodyTemplateMarkdown: t.bodyTemplateMarkdown ?? "",
    bodyDirty: false,
    approvalChain: t.approvalChain ?? null,
    isBorrowerFacing: t.isBorrowerFacing ?? false,
    formAttachmentUpload: null,
    formAttachmentRemoved: false,
    formAttachmentButtonLabel: t.formAttachmentButtonLabel ?? "",
  };
}

function toCreatePayload(state: FormState): CreateCollectionTemplatePayload {
  const includeEducationQuestions =
    state.kind === "Education" && state.educationIncludeQuestions;
  const formSchemaJson =
    state.kind === "Form" || includeEducationQuestions
      ? JSON.stringify(state.schema)
      : null;
  const minDuration =
    state.kind === "Education" && state.educationMinDurationSeconds.trim() !== ""
      ? Number.parseInt(state.educationMinDurationSeconds, 10)
      : null;
  return {
    name: state.name.trim(),
    description: state.description.trim() || null,
    kind: state.kind,
    // Normalize to the sentinel encoding: all seven specifics → ["All"] (spec
    // D1 / §9). The backend re-normalizes as the authority.
    audiences: normalizeAudiences(state.audiences),
    documentTagCode: state.documentTagCode || null,
    instructions: state.instructions.trim() || null,
    formSchemaJson,
    eSignProvider: state.kind === "ESign" ? state.eSignProvider || null : null,
    eSignTemplateRef:
      state.kind === "ESign" ? state.eSignTemplateRef.trim() || null : null,
    signerRoleName:
      state.kind === "ESign" ? state.signerRoleName.trim() || null : null,
    requiresSignature: state.requiresSignature,
    signaturePath: state.requiresSignature ? (state.signaturePath || null) : null,
    feedbackPromptId: null,
    assessmentPromptId: null,
    feedbackEnabled: state.feedbackEnabled,
    assessmentEnabled: state.assessmentEnabled,
    educationContentBlobPath:
      state.kind === "Education" ? state.educationContent?.blobPath ?? null : null,
    educationContentType:
      state.kind === "Education" ? state.educationContent?.contentType ?? null : null,
    educationContentSizeBytes:
      state.kind === "Education" ? state.educationContent?.sizeBytes ?? null : null,
    educationContentOriginalFileName:
      state.kind === "Education"
        ? state.educationContent?.originalFileName ?? null
        : null,
    educationVideoUrl:
      state.kind === "Education" ? state.educationVideoUrl.trim() || null : null,
    educationMinimumDurationSeconds:
      minDuration != null && Number.isFinite(minDuration) ? minDuration : null,
    isSpecialForm: state.kind === "Form" && state.isSpecialForm,
    // Include the body ONLY when actually edited — supplying the key is an
    // explicit body edit requiring Collection.ManageSpecialForms server-side.
    ...(state.kind === "Form" && state.isSpecialForm && state.bodyDirty
      ? { bodyTemplateMarkdown: state.bodyTemplateMarkdown }
      : {}),
    // Approval chains — only when changed (a new template has nothing saved).
    ...(() => {
      const { approvalChain, isBorrowerFacing } = chainChanges(state, null);
      return {
        ...(approvalChain ? { approvalChain } : {}),
        ...(isBorrowerFacing !== undefined ? { isBorrowerFacing } : {}),
      };
    })(),
    // Upload templates (U6a) — the document to download (Upload kind only).
    ...(state.kind === "Upload" && state.formAttachmentUpload
      ? {
          formAttachment: state.formAttachmentUpload,
          formAttachmentButtonLabel: state.formAttachmentButtonLabel.trim() || null,
        }
      : {}),
  };
}

/** Upload templates (U6a) — the document members an update carries, each only
 *  when the user changed it (omitted = carried forward to the new version). */
function formAttachmentChanges(
  state: FormState,
  existing: CollectionTemplateDto,
): Pick<
  UpdateCollectionTemplatePayload,
  "formAttachment" | "clearFormAttachment" | "formAttachmentButtonLabel"
> {
  if (state.kind !== "Upload") return {};
  const out: Pick<
    UpdateCollectionTemplatePayload,
    "formAttachment" | "clearFormAttachment" | "formAttachmentButtonLabel"
  > = {};
  if (state.formAttachmentUpload) out.formAttachment = state.formAttachmentUpload;
  else if (state.formAttachmentRemoved && existing.formAttachmentFileName) {
    out.clearFormAttachment = true;
    return out;
  }
  const label = state.formAttachmentButtonLabel.trim();
  if (label !== (existing.formAttachmentButtonLabel ?? "")) out.formAttachmentButtonLabel = label;
  return out;
}

function toUpdatePayload(
  state: FormState,
  existing: CollectionTemplateDto,
): UpdateCollectionTemplatePayload {
  const create = toCreatePayload(state);
  return {
    name: create.name,
    description: create.description,
    audiences: create.audiences,
    documentTagCode: create.documentTagCode,
    instructions: create.instructions,
    formSchemaJson: create.formSchemaJson,
    eSignProvider: create.eSignProvider,
    eSignTemplateRef: create.eSignTemplateRef,
    signerRoleName: create.signerRoleName ?? null,
    requiresSignature: create.requiresSignature,
    signaturePath: create.signaturePath,
    feedbackPromptId: existing.feedbackPromptId,
    assessmentPromptId: existing.assessmentPromptId,
    feedbackEnabled: state.feedbackEnabled,
    assessmentEnabled: state.assessmentEnabled,
    educationContentBlobPath: create.educationContentBlobPath,
    educationContentType: create.educationContentType,
    educationContentSizeBytes: create.educationContentSizeBytes,
    educationContentOriginalFileName: create.educationContentOriginalFileName,
    educationVideoUrl: create.educationVideoUrl,
    educationMinimumDurationSeconds: create.educationMinimumDurationSeconds,
    isSpecialForm: create.isSpecialForm,
    // CRITICAL — the bodyTemplateMarkdown key must be OMITTED entirely when
    // the body was not edited (undefined serializes away): the server treats
    // a present key (even "") as an admin-gated body edit and 403s
    // non-holders; an absent key carries the current body forward.
    ...("bodyTemplateMarkdown" in create
      ? { bodyTemplateMarkdown: create.bodyTemplateMarkdown }
      : {}),
    // Approval chains — the same rule against the SAVED version: a key is
    // present only when the user changed that member (null = carry forward).
    ...chainChanges(state, existing),
    ...formAttachmentChanges(state, existing),
  };
}

/** A template save / deactivate must reach the admin grid AND the composer's
 *  ["collection-templates","active",partyRole] picker (inactive here — only
 *  stale-marked). Shared with the list page's deactivate; exported for the
 *  invalidation-harness unit test. */
export function invalidateCollectionTemplateReads(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ["collection-templates-admin"] });
  void qc.invalidateQueries({ queryKey: ["collection-templates"] });
}

export function CollectionTemplateEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  // useOptionalAuth (not useAuth) so tests without an AuthProvider degrade
  // to the read-only body view (the AdminUndoButton self-gating precedent).
  const auth = useOptionalAuth();
  const canEditBody = auth?.hasPermission("Collection.ManageSpecialForms") ?? false;
  const canEditChain = auth?.hasPermission("Collection.ManageApprovalChains") ?? false;
  const [state, setState] = useState<FormState>(DEFAULT_FORM);

  const existing = useQuery({
    queryKey: ["collection-template", id],
    queryFn: () => collectionTemplatesApi.get(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (existing.data) setState(fromDto(existing.data));
  }, [existing.data]);

  const existingDto = existing.data ?? null;

  const prompts = useQuery({
    queryKey: ["collection-template-prompts", id],
    queryFn: () => collectionPromptsApi.list(id!),
    enabled: !isNew,
  });

  const tags = useQuery({
    queryKey: ["document-tags"],
    queryFn: () => documentTagsApi.list(),
  });

  // Both mutations take the payload as VARIABLES rather than closing over
  // `state`. useMutation re-syncs its options -- mutationFn included -- in a
  // PASSIVE EFFECT (@tanstack/react-query useMutation.js:21), so a mutationFn
  // that reads component state can run against the previous render's copy if
  // it fires between the DOM commit and that effect. An onClick prop, by
  // contrast, is committed in React's mutation phase, so building the payload
  // in the handler always sees the latest committed state. That is what makes
  // this shape race-free rather than merely usually-right; it is also React
  // Query's documented contract. See #991, which fixed the test-side symptom.
  const create = useMutation({
    mutationFn: (payload: CreateCollectionTemplatePayload) =>
      collectionTemplatesApi.create(payload),
    onSuccess: (dto) => {
      toast({ tone: "success", message: `Template '${dto.name}' created.` });
      void qc.invalidateQueries({ queryKey: ["collection-templates-admin"] });
      navigate(`/admin/collection-templates/${dto.id}`);
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const update = useMutation({
    mutationFn: (payload: UpdateCollectionTemplatePayload) =>
      collectionTemplatesApi.update(id!, payload),
    onSuccess: (dto) => {
      toast({
        tone: "success",
        message: `Saved as v${dto.version}. In-flight items keep their snapshot.`,
      });
      invalidateCollectionTemplateReads(qc);
      // Editing creates a new version with a NEW id — navigate there.
      navigate(`/admin/collection-templates/${dto.id}`);
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const savedChain = existing.data?.approvalChain ?? null;
  const chainErrors = useMemo(
    () =>
      state.approvalChain
        ? validateChain(state.approvalChain, state.isBorrowerFacing, savedChain)
        : [],
    [state.approvalChain, state.isBorrowerFacing, savedChain],
  );
  const validation = useMemo(() => {
    const errors = validate(state);
    if (state.approvalChain && !chainEligible(state)) {
      errors.push("Only a Special form (generated letter) or an E-sign template can carry an approval chain.");
    }
    if (state.isBorrowerFacing && !state.approvalChain) {
      errors.push(
        "A borrower-facing template needs an approval chain (Licensed Intermediary, then Note Holder, before any Borrower stage).",
      );
    }
    return errors;
  }, [state]);
  // The chain's own rule errors render inside the Approval chain section; they
  // block the save like every other validation message.
  const canSave =
    validation.length === 0 && chainErrors.length === 0 && !create.isPending && !update.isPending;

  function patch(p: Partial<FormState>) {
    setState((s) => ({ ...s, ...p }));
  }

  // Collection Templates — Audience Multiselect (spec §3). "All" is a derived
  // master checkbox: it is checked exactly when every specific audience is, and
  // indeterminate when only some are.
  const allAudiencesChecked = SPECIFIC_COLLECTION_AUDIENCES.every((a) =>
    state.audiences.includes(a),
  );
  const someAudiencesChecked = state.audiences.length > 0;
  const allAudiencesRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (allAudiencesRef.current) {
      allAudiencesRef.current.indeterminate =
        someAudiencesChecked && !allAudiencesChecked;
    }
  }, [someAudiencesChecked, allAudiencesChecked]);

  function toggleAudience(a: MaterialAudience) {
    setState((s) => ({
      ...s,
      audiences: s.audiences.includes(a)
        ? s.audiences.filter((x) => x !== a)
        : [...s.audiences, a],
    }));
  }

  function toggleAllAudiences() {
    // Checked (all seven) → clear the selection; otherwise select every
    // audience (spec §3 rules 1 & 2).
    setState((s) => ({
      ...s,
      audiences: SPECIFIC_COLLECTION_AUDIENCES.every((a) => s.audiences.includes(a))
        ? []
        : [...SPECIFIC_COLLECTION_AUDIENCES],
    }));
  }

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Collection · Template"
        title={isNew ? "New collection template" : (existing.data?.name ?? "Edit template")}
        description={
          isNew
            ? "Define how this requestable item behaves. Form / Upload / E-Sign decides which fields to fill in below."
            : `Editing creates a new version. In-flight items keep their snapshot (v${
                existing.data?.version ?? "?"
              }).`
        }
        actions={
          <div className="flex items-center gap-space-2">
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate("/admin/collection-templates")}
            >
              <ArrowLeft size={14} className="mr-space-1" /> Back
            </Button>
            <Button
              type="button"
              disabled={!canSave}
              onClick={() =>
                isNew
                  ? create.mutate(toCreatePayload(state))
                  : update.mutate(toUpdatePayload(state, existing.data!))
              }
            >
              <Save size={14} className="mr-space-1" />
              {isNew ? "Create template" : "Save (new version)"}
            </Button>
          </div>
        }
      />

      {validation.length > 0 && (
        <div className="rounded-md bg-status-error/10 p-space-3 text-small text-onyx">
          <ul className="m-0 pl-space-4">
            {validation.map((v, i) => (
              <li key={i}>{v}</li>
            ))}
          </ul>
        </div>
      )}

      <Card>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-space-3 p-space-4">
          <label className="flex flex-col gap-space-1 text-small text-onyx">
            <span>
              Name <span className="text-error">*</span>
            </span>
            <Input value={state.name} onChange={(e) => patch({ name: e.target.value })} />
          </label>
          <label className="flex flex-col gap-space-1 text-small text-onyx">
            <span>Kind</span>
            <Select
              value={state.kind}
              disabled={!isNew}
              onChange={(e) => {
                const nextKind = e.target.value as CollectionItemKind;
                // Spec §8 decision #5 — ESign templates default both AI
                // toggles OFF on a new template; flipping back to a non-
                // ESign kind restores the default-on. The admin can still
                // override either toggle below.
                const isESign = nextKind === "ESign";
                patch({
                  kind: nextKind,
                  feedbackEnabled: !isESign,
                  assessmentEnabled: !isESign,
                });
              }}
            >
              <option value="Form">Form</option>
              <option value="Upload">Upload</option>
              <option value="ESign">E-Sign (DocuSign)</option>
              <option value="Education">Education / Class</option>
            </Select>
            {!isNew && (
              <span className="text-caption text-gray-mid">
                Kind is fixed once a template exists; create a new template for a different kind.
              </span>
            )}
          </label>
          <div className="flex flex-col gap-space-1 text-small text-onyx">
            <span>
              Audience <span className="text-error">*</span>
            </span>
            <div
              role="group"
              aria-label="Audience"
              className="flex flex-col gap-space-1 rounded-md border border-border-light bg-white-pure p-space-3"
            >
              {SPECIFIC_COLLECTION_AUDIENCES.map((a) => (
                <label key={a} className="flex items-center gap-space-2">
                  <input
                    type="checkbox"
                    checked={state.audiences.includes(a)}
                    onChange={() => toggleAudience(a)}
                    data-testid={`audience-${a}`}
                  />
                  {collectionAudienceLabel(a)}
                </label>
              ))}
              <div className="my-space-1 border-t border-border-light" />
              <label className="flex items-center gap-space-2 font-medium">
                <input
                  ref={allAudiencesRef}
                  type="checkbox"
                  checked={allAudiencesChecked}
                  onChange={toggleAllAudiences}
                  data-testid="audience-all"
                />
                All — everyone; ticking this selects every audience
              </label>
            </div>
            {/* Step 6 W6 — this control and the Portal Materials one are
                different shapes over the same enum: a template targets a SET
                of audiences, a material targets exactly one. Neither is
                wrong; the labels just have to stop implying they are the same
                control. */}
            <span className="text-caption text-gray-mid">
              A template can target several audiences at once. "All" is the
              cross-audience bucket and keeps meaning everyone, including
              audiences added later; specific audiences filter the catalog and
              the composer's template picker. Select at least one.
            </span>
          </div>
          <label className="flex flex-col gap-space-1 text-small text-onyx md:col-span-2">
            <span>Description</span>
            <Textarea
              rows={2}
              value={state.description}
              onChange={(e) => patch({ description: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-space-1 text-small text-onyx">
            <span>Document tag</span>
            <Select
              value={state.documentTagCode}
              onChange={(e) => patch({ documentTagCode: e.target.value })}
            >
              <option value="">(none)</option>
              {(tags.data ?? [])
                .filter((t) => t.isActive)
                .map((t) => (
                  <option key={t.tagCode} value={t.tagCode}>
                    {t.displayName} ({t.tagCode})
                  </option>
                ))}
            </Select>
          </label>
          <div className="flex flex-col gap-space-1 text-small text-onyx">
            <label className="flex items-center gap-space-2">
              <input
                type="checkbox"
                checked={state.requiresSignature}
                onChange={(e) =>
                  patch({
                    requiresSignature: e.target.checked,
                    signaturePath: e.target.checked ? state.signaturePath || "InHouse" : "",
                  })
                }
              />
              Requires signature
            </label>
            {state.requiresSignature && (
              <Select
                value={state.signaturePath}
                onChange={(e) =>
                  patch({ signaturePath: e.target.value as SignaturePath })
                }
                aria-label="Signature path"
              >
                <option value="InHouse">In-house signature module</option>
                <option value="DocuSign">DocuSign</option>
              </Select>
            )}
          </div>
          <div className="flex flex-col gap-space-1 text-small text-onyx">
            <label className="flex items-center gap-space-2">
              <input
                type="checkbox"
                checked={state.isSpecialForm}
                disabled={state.kind !== "Form"}
                onChange={(e) => patch({ isSpecialForm: e.target.checked })}
                data-testid="special-form-toggle"
              />
              Special form (generated letter)
            </label>
            <span className="text-caption text-gray-mid">
              A Form template that renders a letter body with {"{{Token}}"} merge
              fields filled from sender details. Only Form templates can be
              special forms.
            </span>
          </div>
        </CardContent>
      </Card>

      {state.kind === "Form" && (
        <Card>
          <CardContent className="flex flex-col gap-space-3 p-space-4">
            <h2 className="text-h5 font-display text-onyx m-0">Form schema</h2>
            <p className="text-small text-gray-mid m-0">
              Authored once here, rendered to recipients (PR 4) and validated server-side on
              every save (spec §4.1).
            </p>
            <CollectionFormBuilder
              schema={state.schema}
              onChange={(schema) => patch({ schema })}
            />
          </CardContent>
        </Card>
      )}

      {state.kind === "Form" && state.isSpecialForm && (
        <Card>
          <CardContent className="flex flex-col gap-space-3 p-space-4">
            <h2 className="text-h5 font-display text-onyx m-0">Letter body</h2>
            <p className="text-small text-gray-mid m-0">
              The canonical generated-letter body. {"{{Token}}"} merge fields are
              substituted from the sender-field values at render time. Staff can
              override it per send without touching this template.
            </p>
            {!canEditBody ? (
              <>
                <p className="text-small text-gray-mid m-0">
                  Editing the letter body requires the special-forms admin
                  permission.
                </p>
                <Textarea
                  rows={14}
                  value={state.bodyTemplateMarkdown}
                  readOnly
                  disabled
                  aria-label="Letter body (read-only)"
                />
              </>
            ) : isNew || !existingDto ? (
              <>
                <Textarea
                  rows={14}
                  value={state.bodyTemplateMarkdown}
                  onChange={(e) =>
                    patch({ bodyTemplateMarkdown: e.target.value, bodyDirty: true })
                  }
                  aria-label="Letter body"
                />
                <p className="text-caption text-gray-mid m-0">
                  Save the template first to preview the rendered letter.
                </p>
              </>
            ) : (
              <SpecialFormEditor
                mode="template"
                templateId={existingDto.id}
                templateName={existingDto.name}
                formSchemaJson={existingDto.formSchemaJson}
                templateBody={existingDto.bodyTemplateMarkdown ?? ""}
                bodyOverride={state.bodyDirty ? state.bodyTemplateMarkdown : null}
                onBodyOverrideChange={(body) =>
                  body == null
                    ? patch({
                        bodyTemplateMarkdown: existingDto.bodyTemplateMarkdown ?? "",
                        bodyDirty: false,
                      })
                    : patch({ bodyTemplateMarkdown: body, bodyDirty: true })
                }
              />
            )}
          </CardContent>
        </Card>
      )}

      {state.kind === "Upload" && (
        <Card>
          <CardContent className="flex flex-col gap-space-3 p-space-4">
            <h2 className="text-h5 font-display text-onyx m-0">Upload instructions</h2>
            <Textarea
              rows={6}
              value={state.instructions}
              onChange={(e) => patch({ instructions: e.target.value })}
              placeholder="e.g. Upload your most recent two years of personal tax returns (PDF, signed)."
            />
            <p className="text-caption text-gray-mid m-0">
              Accepted formats are managed at the platform level (Word / Excel / PDF / image);
              per-template format filters are not configurable in this PR.
            </p>
          </CardContent>
        </Card>
      )}

      {state.kind === "Upload" && (
        <TemplateFormAttachmentCard
          saved={
            existingDto?.formAttachmentFileName && !state.formAttachmentRemoved
              ? {
                  templateId: existingDto.id,
                  fileName: existingDto.formAttachmentFileName,
                  sizeBytes: existingDto.formAttachmentSizeBytes ?? null,
                  uploadedAt: existingDto.formAttachmentUploadedAt ?? null,
                  uploadedByName: existingDto.formAttachmentUploadedByName ?? null,
                }
              : null
          }
          pending={state.formAttachmentUpload}
          buttonLabel={state.formAttachmentButtonLabel}
          onUploaded={(d) => patch({ formAttachmentUpload: d, formAttachmentRemoved: false })}
          onRemove={() =>
            patch({
              formAttachmentUpload: null,
              formAttachmentRemoved: true,
              formAttachmentButtonLabel: "",
            })
          }
          onButtonLabelChange={(v) => patch({ formAttachmentButtonLabel: v })}
        />
      )}

      {state.kind === "Education" && (
        <Card>
          <CardContent className="flex flex-col gap-space-3 p-space-4">
            <h2 className="text-h5 font-display text-onyx m-0">Education content</h2>
            <p className="text-small text-gray-mid m-0">
              Upload a document and/or paste a video URL. The recipient reviews the content,
              optionally answers questions, and acknowledges they understand it. The
              acknowledgment becomes a staged submission like any other item.
            </p>
            <EducationContentUploader
              current={state.educationContent}
              onChange={(c) => patch({ educationContent: c })}
            />
            <label className="flex flex-col gap-space-1 text-small text-onyx">
              <span>Video URL (optional)</span>
              <Input
                value={state.educationVideoUrl}
                onChange={(e) => patch({ educationVideoUrl: e.target.value })}
                placeholder="https://www.youtube.com/embed/…"
              />
              <span className="text-caption text-gray-mid">
                Either upload a file above or paste a video URL (or both).
              </span>
            </label>
            <label className="flex flex-col gap-space-1 text-small text-onyx">
              <span>Minimum view duration (seconds, optional)</span>
              <Input
                type="number"
                min={0}
                value={state.educationMinDurationSeconds}
                onChange={(e) =>
                  patch({ educationMinDurationSeconds: e.target.value })
                }
                placeholder="e.g. 120"
              />
              <span className="text-caption text-gray-mid">
                If set, the submit button is gated until this many seconds have elapsed
                on the viewer (honor-system mirror of the broker onboarding course).
              </span>
            </label>
            <label className="flex items-center gap-space-2 text-small text-onyx">
              <input
                type="checkbox"
                checked={state.educationIncludeQuestions}
                onChange={(e) =>
                  patch({ educationIncludeQuestions: e.target.checked })
                }
              />
              Include questions for the recipient to answer
            </label>
            {state.educationIncludeQuestions && (
              <div className="border-t border-border-light pt-space-3">
                <CollectionFormBuilder
                  schema={state.schema}
                  onChange={(schema) => patch({ schema })}
                />
              </div>
            )}
            <p className="text-caption text-gray-mid m-0">
              Acknowledgment type is configured above via "Requires signature":
              off = simple attestation checkbox; on = the recipient signs through the
              in-house signature module.
            </p>
          </CardContent>
        </Card>
      )}

      {state.kind === "ESign" && (
        <Card>
          <CardContent className="flex flex-col gap-space-3 p-space-4">
            <h2 className="text-h5 font-display text-onyx m-0">E-Sign configuration</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
              <label className="flex flex-col gap-space-1 text-small text-onyx">
                <span>Provider</span>
                <Select
                  value={state.eSignProvider}
                  onChange={(e) => patch({ eSignProvider: e.target.value })}
                >
                  <option value="DocuSign">DocuSign</option>
                </Select>
              </label>
              <label className="flex flex-col gap-space-1 text-small text-onyx">
                <span>
                  DocuSign template ref <span className="text-error">*</span>
                </span>
                <Input
                  value={state.eSignTemplateRef}
                  onChange={(e) => patch({ eSignTemplateRef: e.target.value })}
                  placeholder="ds-tpl-…"
                />
              </label>
              <label className="flex flex-col gap-space-1 text-small text-onyx">
                <span>Signer role name</span>
                <Input
                  value={state.signerRoleName}
                  onChange={(e) => patch({ signerRoleName: e.target.value })}
                  placeholder="Signer"
                />
                <span className="text-caption text-gray-mid">
                  Must match the role configured on the DocuSign template
                  (defaults to "Signer" when blank).
                </span>
              </label>
            </div>
            <label className="flex flex-col gap-space-1 text-small text-onyx">
              <span>Signer / role instructions (free text)</span>
              <Textarea
                rows={3}
                value={state.instructions}
                onChange={(e) => patch({ instructions: e.target.value })}
                placeholder="e.g. Buyer signs first (Role: Buyer); guarantor signs after (Role: Guarantor)."
              />
            </label>
          </CardContent>
        </Card>
      )}

      {(chainEligible(state) || state.approvalChain !== null) && (
        <Card>
          <CardContent className="flex flex-col gap-space-3 p-space-4">
            <h2 className="text-body font-semibold font-display text-onyx m-0">Approval chain</h2>
            <p className="text-small text-gray-mid m-0">
              The parties who endorse or sign this document, in order, before it is complete.
              Each stage prints its caption and statement on the signature page. Staff can
              adjust the chain on a send, but never remove or move a mandatory stage.
            </p>
            <ApprovalChainEditor
              value={state.approvalChain}
              onChange={(approvalChain) => patch({ approvalChain })}
              isBorrowerFacing={state.isBorrowerFacing}
              onBorrowerFacingChange={(isBorrowerFacing) => patch({ isBorrowerFacing })}
              baseline={savedChain}
              readOnly={!canEditChain}
              errors={chainErrors}
            />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="flex flex-col gap-space-3 p-space-4">
          <h2 className="text-h5 font-display text-onyx m-0">AI prompts</h2>
          <p className="text-small text-gray-mid m-0">
            Both prompts are admin-authored and reused at upload (feedback) + submission
            (assessment). Saved independently; PR 6 wires their execution. Prompts can be
            edited only after the template is saved.
          </p>
          <div className="flex flex-col gap-space-2 border border-border-light rounded-md p-space-3 bg-white-pure">
            <label className="flex items-start gap-space-2 text-small text-onyx">
              <input
                type="checkbox"
                className="mt-space-1"
                checked={state.feedbackEnabled}
                onChange={(e) => patch({ feedbackEnabled: e.target.checked })}
                data-testid="ai-toggle-feedback"
              />
              <span className="flex flex-col">
                <span>Show AI feedback to the recipient</span>
                <span className="text-caption text-gray-mid">
                  When off, the feedback prompt below is ignored at upload / form-review time
                  even if a body is authored.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-space-2 text-small text-onyx">
              <input
                type="checkbox"
                className="mt-space-1"
                checked={state.assessmentEnabled}
                onChange={(e) => patch({ assessmentEnabled: e.target.checked })}
                data-testid="ai-toggle-assessment"
              />
              <span className="flex flex-col">
                <span>Generate an AI assessment for staff</span>
                <span className="text-caption text-gray-mid">
                  When off, the assessment prompt is skipped on submission and the
                  staging queue won't show a distillation.
                </span>
              </span>
            </label>
          </div>
          {isNew ? (
            <p className="text-small text-gray-mid italic">
              Save the template first to author its prompts.
            </p>
          ) : existing.data ? (
            <CollectionPromptEditor
              templateId={existing.data.id}
              templateName={existing.data.name}
              initialPrompts={prompts.data ?? []}
            />
          ) : (
            <p className="text-small text-gray-mid italic">Loading…</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function validate(state: FormState): string[] {
  const errors: string[] = [];
  if (!state.name.trim()) errors.push("Name is required.");
  if (state.audiences.length === 0) errors.push("Select at least one audience.");
  if (state.kind === "Form" && state.schema.fields.length === 0) {
    errors.push("Form templates need at least one field.");
  }
  // An E-Sign template with an approval chain signs each stage on a
  // document-based envelope, so it needs no DocuSign template (the server rule).
  if (state.kind === "ESign" && !state.approvalChain && !state.eSignTemplateRef.trim()) {
    errors.push("E-Sign templates need a DocuSign template ref.");
  }
  if (state.requiresSignature && !state.signaturePath) {
    errors.push("Choose a signature path when 'Requires signature' is on.");
  }
  if (state.kind === "Education") {
    if (!state.educationContent && !state.educationVideoUrl.trim()) {
      errors.push("Education templates need an uploaded file or a video URL.");
    }
    if (state.educationMinDurationSeconds.trim() !== "") {
      const n = Number.parseInt(state.educationMinDurationSeconds, 10);
      if (!Number.isFinite(n) || n < 0) {
        errors.push("Minimum view duration must be a non-negative number.");
      }
    }
    if (state.educationIncludeQuestions && state.schema.fields.length === 0) {
      errors.push("Add at least one question or turn off the questions section.");
    }
  }
  return errors;
}

interface EducationContentUploaderProps {
  current: EducationContentState | null;
  onChange: (next: EducationContentState | null) => void;
}

function EducationContentUploader({ current, onChange }: EducationContentUploaderProps) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  async function handleFile(file: File | null) {
    if (!file) return;
    setBusy(true);
    try {
      const result = await collectionTemplatesApi.uploadEducationContent(file);
      onChange({
        blobPath: result.blobPath,
        contentType: result.contentType,
        sizeBytes: result.sizeBytes,
        originalFileName: result.originalFileName,
      });
      toast({ tone: "success", message: `Uploaded ${result.originalFileName}.` });
    } catch (e) {
      toast({
        tone: "error",
        message: e instanceof Error ? e.message : "Upload failed.",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-space-2">
      <span className="text-small text-onyx">Uploaded content (optional)</span>
      {current ? (
        <div className="flex items-center justify-between p-space-3 border border-border-light rounded-md bg-white-pure">
          <div>
            <p className="text-small text-onyx m-0">{current.originalFileName}</p>
            <p className="text-caption text-gray-mid m-0">
              {current.contentType} · {(current.sizeBytes / 1024).toFixed(0)} KB
            </p>
          </div>
          <Button
            variant="secondary"
            type="button"
            onClick={() => onChange(null)}
            disabled={busy}
          >
            Remove
          </Button>
        </div>
      ) : (
        <input
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,video/*"
          onChange={(e) => void handleFile(e.target.files?.[0] ?? null)}
          disabled={busy}
          className="text-small"
        />
      )}
      {busy && <p className="text-caption text-gray-mid m-0">Uploading…</p>}
    </div>
  );
}
